import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import ImageResize from "tiptap-extension-resize-image";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";
import { fmtBytes } from "@/lib/formato";

/** Maximo permitido por imagen (inline base64). Mas pesadas → ZIP cifrado. */
const MAX_IMG_BYTES = 1024 * 1024;

/** ImageResize maneja el width en el NodeView (DOM live), pero su schema
 * base no declara `width`/`height`/`style` como attrs — `editor.getHTML()`
 * los pierde al serializar. Extendemos el node spec para que esos atributos
 * persistan al HTML guardado. El sanitize backend (sanitize_comentario_html
 * en arktickets/services/html_sanitize.py) ya acepta `width`, `height` y
 * `style` en <img>.
 *
 * Esta es la misma extension que usa GuiaRichEditor — ambas la duplican
 * a proposito para evitar acoplar el editor de correos con el de guias. */
const ImageResizeConPersistencia = ImageResize.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => el.getAttribute("width"),
        renderHTML: (attrs) => (attrs.width ? { width: attrs.width } : {}),
      },
      height: {
        default: null,
        parseHTML: (el) => el.getAttribute("height"),
        renderHTML: (attrs) => (attrs.height ? { height: attrs.height } : {}),
      },
      style: {
        default: null,
        parseHTML: (el) => el.getAttribute("style"),
        renderHTML: (attrs) => (attrs.style ? { style: attrs.style } : {}),
      },
    };
  },
});

type Props = {
  value: string; // HTML
  onChange: (html: string, plain: string) => void;
  placeholder?: string;
  minHeight?: number;
  /** Aplicar autoFocus al montar. */
  autoFocus?: boolean;
  /** Si se provee, el boton imagen del toolbar dispara esto en lugar del
   *  file picker default. El padre puede abrir su propio modal con drop
   *  zone, validacion, upload a evidencias para >1MB, etc. Usa el ref
   *  para insertar el resultado. */
  onImageRequested?: () => void;
  /** Desactiva el drag-drop nativo del editor (no asi el paste). Util
   *  cuando el padre captura el drop a nivel modal para preguntar
   *  inline vs attachment (ej. EnviarCorreoModal). */
  disableImageDrop?: boolean;
};

/** API imperativa expuesta via ref para insertar contenido desde el padre. */
export type RichTextEditorHandle = {
  insertImage: (src: string, alt?: string) => void;
  insertHtml: (html: string) => void;
  focus: () => void;
};

/**
 * Editor WYSIWYG basado en TipTap (StarterKit + Link + ImageResize inline).
 *
 * Toolbar: B, I, S, lista, lista numerada, blockquote, link, imagen, limpiar.
 * Imagenes (default): file picker, validacion de 1MB, base64 inline. Si se
 * provee `onImageRequested`, el padre toma control (ej. modal drop-zone con
 * fallback a evidencias para imagenes pesadas).
 */
const RichTextEditor = forwardRef<RichTextEditorHandle, Props>(function RichTextEditor(
  { value, onChange, placeholder, minHeight = 220, autoFocus, onImageRequested, disableImageDrop },
  ref,
) {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
      // Image con handles de resize en las esquinas. Reemplaza al Image
      // default — comparte el comando `setImage` (insertImage del handle).
      ImageResizeConPersistencia,
    ],
    content: value || "",
    autofocus: autoFocus ? "end" : false,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML(), editor.getText());
    },
    editorProps: {
      attributes: {
        class:
          "tiptap-content max-w-none focus:outline-none px-3 py-2 text-sm text-zinc-100",
        style: `min-height: ${minHeight}px;`,
        "data-placeholder": placeholder ?? "Escribe aquí...",
      },
    },
  });

  // Sync externo -> editor (ej. al reusar un comentario del historial)
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() === value) return;
    editor.commands.setContent(value || "", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const insertImage = useCallback(
    async (file: File) => {
      if (!editor) return;
      if (!file.type.startsWith("image/")) {
        toast.error("Ese archivo no es imagen", file.type);
        return;
      }
      if (file.size > MAX_IMG_BYTES) {
        // Si el padre paso onImageRequested, abrirlo (modal con drop-zone +
        // upload a Evidencias para imagenes pesadas). Si no, error claro.
        if (onImageRequested) {
          onImageRequested();
          toast.info(
            `Imagen de ${fmtBytes(file.size)} excede el límite dentro del mensaje (1 MB)`,
            'Abriendo la ventana de Evidencias — arrastra el archivo ahí y se sube cifrado.',
          );
          return;
        }
        toast.error(
          `Imagen muy grande (${fmtBytes(file.size)})`,
          "Máximo 1MB dentro del mensaje. Para imágenes más grandes usa Evidencias (ZIP cifrado)",
        );
        return;
      }
      const dataUrl: string = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = () => reject(r.error);
        r.readAsDataURL(file);
      });
      editor.chain().focus().setImage({ src: dataUrl, alt: file.name }).run();
    },
    [editor, toast, onImageRequested],
  );

  // Drag-and-drop + paste de imagenes directo en el editor.
  // - Drag: arrastrar archivo de imagen al area del editor inserta inline
  // - Paste: Ctrl+V de imagen del clipboard inserta inline
  // Aplica el mismo limite (1MB) que el file picker. Para imagenes mas
  // grandes el padre pasa onImageRequested (modal con drop-zone + Evidencias).
  useEffect(() => {
    if (!editor) return;
    const dom = editor.view.dom;

    // Si el padre desactiva drop (ej. modal de correo que pregunta
    // inline vs adjunto), saltamos el registro de drag/drop. El paste
    // sigue funcionando — pegar imagen al cursor es UX esperado.
    const dragHandlersOff = disableImageDrop === true;

    const onDragOver = (e: DragEvent) => {
      if (dragHandlersOff) return;
      if (e.dataTransfer?.types?.includes("Files")) {
        e.preventDefault();
      }
    };

    const onDrop = (e: DragEvent) => {
      if (dragHandlersOff) return;
      const files = e.dataTransfer?.files;
      if (!files?.length) return;
      const file = files[0];
      if (!file.type.startsWith("image/")) return;
      e.preventDefault();
      e.stopPropagation();
      void insertImage(file);
    };

    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            e.preventDefault();
            void insertImage(file);
            return;
          }
        }
      }
    };

    dom.addEventListener("dragover", onDragOver);
    dom.addEventListener("drop", onDrop);
    dom.addEventListener("paste", onPaste);
    return () => {
      dom.removeEventListener("dragover", onDragOver);
      dom.removeEventListener("drop", onDrop);
      dom.removeEventListener("paste", onPaste);
    };
  }, [editor, insertImage, disableImageDrop]);

  function onFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) void insertImage(f);
    e.target.value = "";
  }

  function clickImagen() {
    if (onImageRequested) {
      onImageRequested();
    } else {
      fileInputRef.current?.click();
    }
  }

  // API imperativa expuesta al padre (TabComentario abre su modal y luego
  // inserta el resultado via este handle).
  useImperativeHandle(
    ref,
    () => ({
      insertImage: (src: string, alt?: string) => {
        editor?.chain().focus().setImage({ src, alt: alt ?? "" }).run();
      },
      insertHtml: (html: string) => {
        editor?.chain().focus().insertContent(html).run();
      },
      focus: () => {
        editor?.commands.focus();
      },
    }),
    [editor],
  );

  function abrirLinkModal() {
    if (!editor) return;
    const previo = editor.getAttributes("link").href || "";
    setLinkUrl(previo);
    setLinkOpen(true);
  }

  function aplicarLink() {
    if (!editor) return;
    const url = linkUrl.trim();
    if (!url) {
      editor.chain().focus().unsetLink().run();
    } else {
      // Asegura protocolo
      const final = /^https?:|^mailto:/.test(url) ? url : `https://${url}`;
      editor.chain().focus().extendMarkRange("link").setLink({ href: final }).run();
    }
    setLinkOpen(false);
  }

  if (!editor) {
    return (
      <div className="card text-sm text-zinc-500" style={{ minHeight }}>
        Cargando editor...
      </div>
    );
  }

  return (
    <div className="rounded-md border border-ink-700 bg-ink-900 overflow-hidden">
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-ink-700 bg-ink-800/50">
        <ToolbarBtn
          nombre="Negrita"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <span className="font-bold">B</span>
        </ToolbarBtn>
        <ToolbarBtn
          nombre="Itálica"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <span className="italic">I</span>
        </ToolbarBtn>
        <ToolbarBtn
          nombre="Tachado"
          active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <span className="line-through">S</span>
        </ToolbarBtn>
        <Sep />
        <ToolbarBtn
          nombre="Lista con viñetas"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          •
        </ToolbarBtn>
        <ToolbarBtn
          nombre="Lista numerada"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          1.
        </ToolbarBtn>
        <ToolbarBtn
          nombre="Cita"
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          ❝
        </ToolbarBtn>
        <Sep />
        <ToolbarBtn
          nombre={editor.isActive("link") ? "Editar o quitar enlace" : "Insertar enlace"}
          active={editor.isActive("link")}
          onClick={abrirLinkModal}
        >
          🔗
        </ToolbarBtn>
        <ToolbarBtn
          nombre="Insertar imagen"
          onClick={clickImagen}
        >
          🖼
        </ToolbarBtn>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onFileChosen}
        />
        <Sep />
        <ToolbarBtn
          nombre="Limpiar formato"
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
        >
          ⌫
        </ToolbarBtn>
      </div>

      <EditorContent editor={editor} />

      <Modal
        open={linkOpen}
        onOpenChange={(v) => !v && setLinkOpen(false)}
        title="Enlace"
        description="Pega o escribe la dirección. Vacío quita el enlace."
        size="sm"
        footer={
          <>
            <button onClick={() => setLinkOpen(false)} className="btn-ghost">
              Cancelar
            </button>
            <button onClick={aplicarLink} className="btn-primary">
              Aplicar
            </button>
          </>
        }
      >
        <input
          type="text"
          autoFocus
          className="input"
          value={linkUrl}
          placeholder="https://ejemplo.com"
          onChange={(e) => setLinkUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              aplicarLink();
            }
          }}
        />
      </Modal>
    </div>
  );
});

export default RichTextEditor;

function Sep() {
  return <span className="w-px h-5 bg-ink-700 mx-1" />;
}

/**
 * Botón de la barra. Es puro ícono: `nombre` va en `aria-label` para el lector
 * de pantalla. Lo que antes explicaba el tooltip (incluidos los atajos Ctrl+B /
 * Ctrl+I) vive en la Ayuda de la ventana que usa el editor
 * (`components/ayuda/vistas/redaccionComun`: `botonesEditor`, `ATAJOS_EDITOR`).
 */
function ToolbarBtn({
  children,
  active,
  nombre,
  onClick,
}: {
  children: React.ReactNode;
  active?: boolean;
  nombre: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={nombre}
      className={`min-w-[28px] h-7 px-2 text-sm rounded transition ${
        active
          ? "bg-accent-500/20 text-accent-300 border border-accent-500/40"
          : "text-zinc-300 hover:text-white hover:bg-ink-700 border border-transparent"
      }`}
    >
      {children}
    </button>
  );
}
